import { Button, Group, Modal, Stack, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { upperFirst } from "@mantine/hooks"
import { LabelWithTooltip, Switch, TextInput } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { SyntheticEvent, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import z from "zod"

import { queryClient } from "~/backend/queryClient"
import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"
import {
  findViewByIdQueryKey,
  findViewsQueryKey,
  useCreateView,
  useUpdateView,
} from "~/gen/api"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type AddAndUpdateViewModalProps = {
  opened: boolean
  onClose: () => void
  tableContext: string
}

const addViewSchema = z.object({
  name: z.string().min(1).max(32),
  pinnedToDashboard: z.boolean().default(false),
})

export const AddAndUpdateViewModal = ({
  opened,
  onClose,
  tableContext,
}: AddAndUpdateViewModalProps) => {
  const { t } = useTranslation("common")
  const [searchParams, setSearchParams] = useSearchParams()
  const viewId = searchParams.get("viewId") ?? ""
  const isUpdating = !!viewId
  const {
    view,
    filters,
    sorts,
    columnPinning,
    columnVisibility,
    orderedColumns,
  } = useHeaderControlsContext()
  const { mutate: createView } = useCreateView()
  const { mutate: updateView } = useUpdateView()

  const form = useForm({
    initialValues: {
      name: view?.name ?? "",
      pinnedToDashboard: view?.pinnedToDashboard ?? false,
    },
    validate: zod4Resolver(addViewSchema),
    mode: "uncontrolled",
  })

  const handleCreateView = (values: z.infer<typeof addViewSchema>) => {
    createView(
      {
        data: {
          name: values.name,
          tableContext: tableContext ?? "general",
          filters: filters ?? {},
          sorts: sorts ?? {},
          pinnedColumns: {
            columnOrder: orderedColumns,
            columnPinning,
            columnVisibility,
          },
          pinnedToDashboard: values.pinnedToDashboard,
        },
      },
      {
        onSuccess: (response) => {
          const data = response.data as unknown as string
          setSearchParams((prev) => {
            prev.set("viewId", data)
            return prev
          })
          queryClient.invalidateQueries({
            queryKey: findViewsQueryKey({
              tableContext,
            }),
          })
          showSuccessNotification({
            title: t("table.views.add.success.title"),
            message: t("table.views.add.success.message"),
          })
          form.reset()
          onClose()
        },
        onError: (error) => {
          showAxiosError({
            error,
            showNotification: true,
            defaultMessage: t("table.views.add.error.message"),
            notificationTitle: t("table.views.add.error.title"),
          })
        },
      }
    )

    onClose()
  }

  const handleUpdateView = (values: z.infer<typeof addViewSchema>) => {
    if (!view) return
    addViewSchema.parse(values)
    updateView(
      {
        id: view.id,
        data: {
          id: view.id,
          name: values.name,
          tableContext,
          filters: filters ?? {},
          sorts: sorts ?? {},
          pinnedColumns: {
            columnOrder: orderedColumns,
            columnPinning,
            columnVisibility,
          },
          pinnedToDashboard: values.pinnedToDashboard,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: findViewsQueryKey({
              tableContext,
            }),
          })
          queryClient.invalidateQueries({
            queryKey: findViewByIdQueryKey(view.id),
          })
          showSuccessNotification({
            title: t("table.views.update.success.title"),
            message: t("table.views.update.success.message"),
          })
          form.reset()
          onClose()
        },
        onError: (error) => {
          showAxiosError({
            error,
            showNotification: true,
            defaultMessage: t("table.views.update.error.message"),
            notificationTitle: t("table.views.update.error.title"),
          })
        },
      }
    )
    onClose()
  }

  const handleSubmit = (
    values: z.infer<typeof addViewSchema>,
    event: SyntheticEvent<HTMLFormElement, SubmitEvent> | undefined
  ) => {
    if (!event) return
    event.preventDefault()
    handleCreateView(values)
  }

  useEffect(() => {
    const viewName = view?.name
    if (viewName && !opened) {
      form.setFieldValue("name", viewName)
    }
  }, [form, view?.name, opened])

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("table.views.modal.title")}
    >
      <Stack>
        <Text>{t("table.views.modal.description")}</Text>

        <form
          onSubmit={form.onSubmit((values, event) =>
            handleSubmit(
              values,
              event as SyntheticEvent<HTMLFormElement, SubmitEvent> | undefined
            )
          )}
        >
          <Stack>
            <TextInput
              label={t("table.views.viewName")}
              placeholder={t("table.views.viewNamePlaceholder")}
              required
              error={form.errors.name}
              {...form.getInputProps("name")}
            />
            <Switch
              inlineLabel
              label={
                <LabelWithTooltip
                  label={t("table.views.modal.pinnedToDashboard.label")}
                  hoverHelperText={t(
                    "table.views.modal.pinnedToDashboard.tooltip"
                  )}
                  fw={600}
                  fz="xs"
                  justify="start"
                />
              }
              checked={form.values.pinnedToDashboard}
              onChange={(checked) =>
                form.setFieldValue("pinnedToDashboard", checked)
              }
            />
            <Group grow>
              {isUpdating && (
                <Button
                  fullWidth
                  variant="secondary"
                  name="update"
                  onClick={() => handleUpdateView(form.getValues())}
                  disabled={!form.isValid()}
                >
                  {upperFirst(t("table.views.modal.update"))}
                </Button>
              )}
              <Button
                fullWidth
                type="submit"
                variant="primary"
                name="save"
                disabled={!form.isValid()}
              >
                {upperFirst(t("table.views.modal.save"))}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Modal>
  )
}
